import * as Sentry from '@sentry/angular';
import {
  ApplicationConfig,
  provideZoneChangeDetection,
  ErrorHandler,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  provideRouter,
  withRouterConfig,
  withInMemoryScrolling,
  Router,
} from '@angular/router';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { environment } from '@env/environment';
import {
  HttpBackend,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { authInterceptor } from '@app/interceptors/auth.interceptor';
import { openReplayInterceptor } from '@app/interceptors/open-replay.interceptor';
import { MessageService } from 'primeng/api';
import {
  InMemoryBackendConfig,
  InMemoryDbService,
} from 'angular-in-memory-web-api';
import { InMemHeroService } from '@services/in-memory-data/in-memory-data.service';
import { InMemoryPassthruBackendService } from '@services/in-memory-data/in-memory-passthru-backend.service';
import { provideAuth, LogLevel } from 'angular-auth-oidc-client';
import { app_main_routing_menu } from '@app/shared/app-main-routing-menu';
import { ENVIRONMENT_NAMES } from '@shared/constants/constants';
import { primeNg_strings } from '@app/shared/strings/common.strings';
import { StiristaPreset } from 'stirista-gui-kit';

// import { CacheService } from '@services/cache.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // Clearing cache at page load is not needed for now, left here for future reference
    // provideAppInitializer(() => inject(CacheService).clearCacheForApp()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        // https://angular.dev/api/router/RouterConfigOptions#api
        canceledNavigationResolution: 'computed',
      }),
      withInMemoryScrolling({
        // On route change, scroll the view to the top.
        scrollPositionRestoration: 'top',
        // On navigation to a URL with #id, scroll to the element with that id.
        anchorScrolling: 'enabled',
      }),
    ),

    provideAuth({
      config: {
        configId: environment.accessIdentity.configId,
        authority: environment.accessIdentity.authority,
        redirectUrl: `${window.location.origin}/${app_main_routing_menu.signin.path}`,
        postLogoutRedirectUri: `${window.location.origin}/${app_main_routing_menu.login.path}`,
        clientId: environment.accessIdentity.clientId,
        scope: 'openid accessIdentity offline_access',
        responseType: 'code',
        logLevel: environment.production ? LogLevel.None : LogLevel.Debug,
        autoUserInfo: false,
        silentRenew: false,
        historyCleanupOff: false,
        startCheckSession: false,
      },
    }),
    provideHttpClient(
      withInterceptors(
        environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
          environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
          ? [authInterceptor]
          : [authInterceptor, openReplayInterceptor],
      ),
    ), // In-Memory Web API (development only).
    // Providers are wired manually (instead of `HttpClientInMemoryWebApiModule.forRoot(...)`)
    // so we can substitute `HttpBackend` with `InMemoryPassthruBackendService`,
    // which resolves the pass-thru backend through Angular DI and avoids the
    // Angular 21 NG0203 triggered by `new HttpXhrBackend(...)` in the upstream
    // library.
    ...(environment.useInMemoryApi
      ? [
          { provide: InMemoryDbService, useClass: InMemHeroService },
          {
            provide: InMemoryBackendConfig,
            useValue: {
              apiBase: environment.apiMockUrl,
              dataEncapsulation: false,
              delay: 600,
              passThruUnknownUrl: true,
            },
          },
          { provide: HttpBackend, useClass: InMemoryPassthruBackendService },
        ]
      : []),
    providePrimeNG({
      theme: {
        preset: StiristaPreset,
        options: {
          darkModeSelector: '.' + primeNg_strings.dark_mode_class_name,
          // IMPORTANT! DO NOT CHANGE THE PREFIX. IT MUST MATCH THE DEFAULT PRIMENG PREFIX.
          // OTHERWISE SOME COMPONENTS FROM THE KIT MAY NOT APPLY THE STYLING CORRECTLY
          prefix: 'p',
        },
      },
      // The default overlay changed and it's not body anymore
      // This is a workaround for a bug in PrimeNG https://github.com/primefaces/primeng/issues/19404#issuecomment-4114238929
      overlayAppendTo: 'body',
    }),
    MessageService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
